import { useEffect, useMemo, useState } from "react";
import {
  moneyINR,
  prettyDate,
  statusLabel,
  statusTone,
  isPayEnabled,
  cn,
} from "./invoiceUtils";
import { Badge } from "../../../../Component/RetailerComponents/invoice/Badge";
import { Stat } from "../../../../Component/RetailerComponents/invoice/Stat";
import { InternalButton } from "../../../../Component/RetailerComponents/invoice/InternalButton";
import { PrimaryLinkButton } from "../../../../Component/RetailerComponents/invoice/PrimaryLinkButton";
import { SecondaryLinkButton } from "../../../../Component/RetailerComponents/invoice/SecondaryLinkButton";
import Pagination from "../../../../Component/Common/Pagination";
import { BASE_URL } from "../../../../config/api";
import toast from "react-hot-toast";
import BlueButton from "../../../../Component/Common/BlueButton";
import { useNavigate } from "react-router-dom";

export default function InvoiceList({
  invoices,
  currentPage,
  totalPages,
  onPageChange,
  search,
  setSearch,
  invoiceStatus,
  setInvoiceStatus,
}) {

  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc");

  const [paymentKeys, setPaymentKeys] = useState({
          key_id: "",
          key_secret: ""
        });

  const [keyLoading, setKeyLoading] = useState(false);
    const [paymentSaving, setPaymentSaving] = useState(false);

    const authUserString = localStorage.getItem("authUserData");
    const authUser = authUserString ? JSON.parse(authUserString) : null;
    const sessionId = authUser?.id;
    const role = authUser?.role;

    const isRetailerMobile =
    window.innerWidth < 640 && role === 5;

  /* ---------- customers ---------- */
  const customers = useMemo(() => {
    const map = new Map();
    invoices.forEach((inv) => {
      if (!map.has(inv.customer_id)) {
        map.set(inv.customer_id, {
          customer_id: inv.customer_id,
          name: inv.customer_name,
        });
      }
    });
    return Array.from(map.values());
  }, [invoices]);

  /* ---------- filtered ---------- */
  const filtered = useMemo(() => {
    let out = invoices.slice();
    const needle = q.trim().toLowerCase();

    if (customerFilter !== "all") {
      out = out.filter((x) => x.customer_id === customerFilter);
    }

    if (statusFilter !== "all") {
      out = out.filter(
        (x) => statusLabel(x).toLowerCase() === statusFilter
      );
    }

    if (needle) {
      out = out.filter((x) =>
        [
          x.invoice_number,
          x.reference_number,
          x.customer_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
    }

    out.sort((a, b) => {
      if (sortBy === "date_asc") return new Date(a.date) - new Date(b.date);
      if (sortBy === "total_desc") return b.total - a.total;
      if (sortBy === "total_asc") return a.total - b.total;
      return new Date(b.date) - new Date(a.date);
    });

    return out;
  }, [invoices, q, statusFilter, customerFilter, sortBy]);

  /* ---------- KPIs ---------- */
  const kpis = useMemo(() => {
    const sum = (fn) => filtered.reduce((a, x) => a + fn(x), 0);
    return {
      total: sum((x) => x.total),
      paid: sum((x) => x.payment_made),
      balance: sum((x) => x.balance),
      count: filtered.length,
      overdueCount: filtered.filter(
        (x) => statusLabel(x) === "OVERDUE"
      ).length,
    };
  }, [filtered]);

function Icon({ name, className }) {
  const common = "fill-none stroke-current";
  const props = {
    className: cn("h-5 w-5", className),
    viewBox: "0 0 24 24",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  if (name === "link")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10.5 4.43" />
        <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.4a5 5 0 0 0 7.07 7.07l.9-.9" />
      </svg>
    );

  if (name === "copy")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <rect x="9" y="9" width="13" height="13" rx="2" />
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
      </svg>
    );

  if (name === "check")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );

  if (name === "user")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <path d="M20 21a8 8 0 1 0-16 0" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );

  if (name === "receipt")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <path d="M7 3h10a2 2 0 0 1 2 2v16l-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 0 1 2-2z" />
        <path d="M9 7h6" />
        <path d="M9 11h6" />
        <path d="M9 15h4" />
      </svg>
    );

  if (name === "pin")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <path d="M12 22s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
        <circle cx="12" cy="11" r="2.5" />
      </svg>
    );

  if (name === "clock")
    return (
      <svg {...props} className={cn(props.className, common)}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6l4 2" />
      </svg>
    );

  return (
    <svg {...props} className={cn(props.className, common)}>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}


const fetchPaymentKeys = async () => {
    try {
      setKeyLoading(true);
  
      const res = await fetch(`${BASE_URL}payment/keys`);
      const data = await res.json();
  
      if (Array.isArray(data) && data.length > 0) {
        setPaymentKeys({
          key_id: data[0].key_id,
          key_secret: data[0].key_secret
        });
      } else {
        console.error("Invalid keys response", data);
      }
    } catch (err) {
      console.error("Failed to fetch payment keys", err);
    } finally {
      setKeyLoading(false);
    }
  };
  
    useEffect(() => {
      fetchPaymentKeys();
  }, []);

  const handlePayNow = async (inv) => {
  if (!paymentKeys?.key_id) {
    toast.error("Payment keys not loaded");
    return;
  }

  // 💰 amount from invoice row
  const amount = Number(inv.balance || 0);
  console.log("amount: ", amount)
  const roundedAmount = Math.round(amount * 100); // paise

  // 🧾 Notes built from ROW
  const notes = {
    project: "warranty",
    service: "update_pay_later",
    // imei1: inv.imei1,
    // imei2: inv.imei2,
    // serial:inv.serial,
    // product_id:inv.product_id,
    // model_id:inv.model_id,
    company_id: authUser?.company_id,
    retailer_id: sessionId,
    user_id: sessionId,
    retailer_code:authUser?.company_code,
    retailer_name: authUser?.business_name,
    agent_id: authUser?.agent_id,
    amount: amount,
    // warranty_id: row.warrantyId || null,
    invoice_id: inv.invoice_id || null,
    invoice_code: inv.invoice_number|| null,
    // w_customer_id:inv.w_customer_id,
    created_by: sessionId,
    // document_url: inv.billPhoto,
    // device_id: inv.device_id,
    zoho_id: authUser?.zoho_id || null,
  };

  const options = {
    key: paymentKeys.key_id,
    amount: roundedAmount,
    currency: "INR",
    name: "GO Electronix",
    description: `Invoice Payment - ${inv.invoice_Code || inv.id}`,
    notes,

    handler: async function (response) {
      try {
        setPaymentSaving(true);

        console.log("✅ Razorpay Success:", response);
        console.log("📦 Paid Row:", inv);

        const payload = {
          payment_id: response.razorpay_payment_id,

          invoice_id: inv.id,
          invoice_code: inv.invoice_Code,

          amount,
          customer_name: inv.customerName,
          customer_code: inv.customerCode,

          retailer_id: sessionId,
          company_id: authUser?.company_id,

          source: "invoice_pay_now",
        };

        console.log("🚀 Payment Payload:", payload);

        toast.success("Payment successful 🎉");
        window.location.reload()

        // 🔁 Refresh invoices after payment
        // fetchInvoices();
        // fetchPayoutSummary();
      } catch (err) {
        console.error(err);
        toast.error("Payment succeeded but processing failed");
      } finally {
        setPaymentSaving(false);
      }
    },

    prefill: {
      name: inv.customerName || "",
      contact: inv.phone || "",
    },

    theme: { color: "#2563eb" },
  };

  const rzp = new window.Razorpay(options);
  rzp.open();
};

  return (
    <div className="transition-all duration-300">
      {/* 🔁 COPY YOUR EXISTING LIST JSX HERE */}

        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-sky-500 opacity-90" />
          <div className="absolute -top-20 -left-20 h-72 w-72 rounded-full bg-white/15 blur-2xl" />
          <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />

          <div className="relative mx-auto  px-4 pb-6 pt-6 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 shadow-sm">
                  <Icon name="receipt" className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold text-white">Invoices</h1>
                  <div className="mt-1 text-sm text-white/85">Search • Filter • Pay Now</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge tone="slate">Count: {kpis.count}</Badge>
                <Badge tone={kpis.overdueCount ? "red" : "green"}>Overdue: {kpis.overdueCount}</Badge>
              </div>
            </div>

            {/* <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Total" value={moneyINR(kpis.total)} sub="Filtered total" />
              <Stat label="Paid" value={moneyINR(kpis.paid)} sub="Payments received" />
              <Stat label="Balance" value={moneyINR(kpis.balance)} sub="Pending dues" />
              <Stat label="Customers" value={String(customers.length)} sub="Unique" />
            </div> */}
          </div>
        </div>

        {/* Filters */}
        <div className="mx-auto  py-3 sm:px-4">
          <div
            className={cn(
              "rounded-3xl overflow-hidden transition-all duration-300",
              isRetailerMobile
                ? "glass-card bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)] "
                : "bg-white shadow-sm border border-slate-200/60"
            )}
          >
            <div className="p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="w-full lg:max-w-lg">
                  
                </div>

                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 justify-end lg:w-auto">
                  {/* <div>
                    <label className="text-xs font-bold text-slate-600">Customer</label>
                    <select
                      value={customerFilter}
                      onChange={(e) => setCustomerFilter(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
                    >
                      <option value="all">All Customers</option>
                      {customers.map((c) => (
                        <option key={c.customer_id} value={c.customer_id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  <div
                    className={cn(
                      "grid gap-3",
                      isRetailerMobile ? "grid-cols-1" : "grid-cols-2"
                    )}
                  >
                    {/* SEARCH */}
                    <div>
                      <label
                        className={cn(
                          "text-xs font-semibold mb-1 block",
                          isRetailerMobile ? "text-slate-600" : "text-slate-600"
                        )}
                      >
                        Search
                      </label>

                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Invoice number (min 4 letters)..."
                        className={cn(
                          "w-full rounded-2xl px-4 py-3 text-sm font-semibold outline-none transition-all duration-300",
                          isRetailerMobile
                            ? "bg-white/10 backdrop-blur-xl border border-white/30 text-slate-800 placeholder:text-slate-500 shadow-[0_15px_35px_rgba(31,38,135,0.15)] focus:bg-white/20"
                            : "border border-slate-200 bg-slate-50 text-slate-900 focus:bg-white"
                        )}
                      />
                    </div>

                    {/* STATUS */}
                    <div>
                      <label
                        className={cn(
                          "text-xs font-semibold mb-1 block",
                          isRetailerMobile ? "text-slate-600" : "text-slate-600"
                        )}
                      >
                        Status
                      </label>

                      <select
                        value={invoiceStatus}
                        onChange={(e) => {
                          setInvoiceStatus(e.target.value);
                          onPageChange(1);
                        }}
                        className={cn(
                          "w-full rounded-2xl px-3 py-3 text-sm font-semibold outline-none transition-all duration-300",
                          isRetailerMobile
                            ? "bg-white/10 backdrop-blur-xl border border-white/30 text-slate-800 shadow-[0_15px_35px_rgba(31,38,135,0.15)] focus:bg-white/20"
                            : "border border-slate-200 bg-slate-50 text-slate-900"
                        )}
                      >
                        <option value="all">All</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="partial">Partial</option>
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                      </select>
                    </div>
                  </div>

                  {/* <div>
                    <label className="text-xs font-bold text-slate-600">Sort</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
                    >
                      <option value="date_desc">Date (Newest)</option>
                      <option value="date_asc">Date (Oldest)</option>
                      <option value="total_desc">Amount (High)</option>
                      <option value="total_asc">Amount (Low)</option>
                    </select>
                  </div> */}
                </div>
              </div>

              {/* MOBILE LIST (cards) */}
              <div className="mt-4 grid gap-3 lg:hidden">
               {filtered.length === 0 ? (
                  <div
                    className={cn(
                      "rounded-2xl p-6 text-center transition-all duration-300",
                      isRetailerMobile
                        ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
                        : "border border-slate-200/60 bg-slate-50"
                    )}
                  >
                    <div
                      className={cn(
                        "text-sm font-extrabold",
                        isRetailerMobile ? "text-slate-800" : "text-slate-900"
                      )}
                    >
                      No invoices found
                    </div>

                    <div
                      className={cn(
                        "mt-1 text-xs",
                        isRetailerMobile ? "text-slate-600" : "text-slate-500"
                      )}
                    >
                      Try clearing filters or changing search terms.
                    </div>
                  </div>
                ) : (
                  filtered.map((inv) => {
                    const isAlreadyPaid =
                      statusLabel(inv) === "PAID" || statusLabel(inv) === "DRAFT";

                    return (
                      <div
                        key={inv.invoice_id}
                        className={cn(
                          "rounded-3xl p-4 transition-all duration-300",
                          isRetailerMobile
                            ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
                            : "bg-white border border-slate-200/60 shadow-sm"
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <button
                            onClick={() => {
                              navigate(`/invoices/${inv.invoice_id}`)
                              window.scrollTo?.(0, 0);
                            }}
                            className="text-left"
                          >
                            <div className="text-sm font-extrabold text-slate-900">
                              {inv.invoice_number}
                            </div>
                            <div className="mt-0.5 text-xs text-slate-500 whitespace-wrap">
                              {inv.customer_name}
                            </div>
                          </button>
                          <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                          <div className={cn(
                              "rounded-2xl p-3 transition-all duration-300",
                              isRetailerMobile
                                ? "bg-white/10 backdrop-blur-lg border border-white/20"
                                : "bg-slate-50 border border-slate-200/60"
                            )}>
                            <div className="text-[11px] text-slate-500 font-bold">Date</div>
                            <div className="mt-0.5 font-semibold text-slate-900">
                              {prettyDate(inv.date)}
                            </div>
                          </div>
                          <div className={cn(
                              "rounded-2xl p-3 transition-all duration-300",
                              isRetailerMobile
                                ? "bg-white/10 backdrop-blur-lg border border-white/20"
                                : "bg-slate-50 border border-slate-200/60"
                            )}>
                            <div className="text-[11px] text-slate-500 font-bold">Total</div>
                            <div className="mt-0.5 font-extrabold text-slate-900">
                              {moneyINR(inv.total)}
                            </div>
                          </div>
                          <div className={cn(
                              "rounded-2xl p-3 transition-all duration-300",
                              isRetailerMobile
                                ? "bg-white/10 backdrop-blur-lg border border-white/20"
                                : "bg-slate-50 border border-slate-200/60"
                            )}>
                            <div className="text-[11px] text-slate-500 font-bold">Paid</div>
                            <div className="mt-0.5 font-semibold text-slate-900">
                              {moneyINR(inv.payment_made)}
                            </div>
                          </div>
                          <div className={cn(
                              "rounded-2xl p-3 transition-all duration-300",
                              isRetailerMobile
                                ? "bg-white/10 backdrop-blur-lg border border-white/20"
                                : "bg-slate-50 border border-slate-200/60"
                            )}>
                            <div className="text-[11px] text-slate-500 font-bold">Balance</div>
                            <div
                              className={cn(
                                "mt-0.5 font-extrabold",
                                Number(inv.balance || 0) === 0
                                  ? "text-emerald-700"
                                  : "text-rose-700"
                              )}
                            >
                              {moneyINR(inv.balance)}
                            </div>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="text-xs text-slate-500 truncate">
                            Ref: {inv.reference_number}
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <InternalButton
                              size="sm"
                              label="View"
                              onClick={() => {
                              navigate(`/invoices/${inv.invoice_id}`)
                                window.scrollTo?.(0, 0);
                              }}
                            />

                            {!isAlreadyPaid && role === 5 && (
                              <BlueButton
                                size="sm"
                                label="Pay Now"
                                disabled={!isPayEnabled(inv)}
                                onClick={() => handlePayNow(inv)}
                              />
                            )}

                            {/* <SecondaryLinkButton
                              href={inv.invoice_url}
                              label="Download"
                              size="sm"
                            /> */}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

              </div>

              {/* DESKTOP TABLE */}
              <div className="mt-4 hidden lg:block overflow-hidden rounded-2xl border border-slate-200/60">
                <div className="grid grid-cols-12 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
                  <div className="col-span-3">Invoice</div>
                  <div className="col-span-2">Customer</div>
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2 text-center">Total</div>
                  <div className="col-span-3 text-right">Actions</div>
                </div>

                <div className="divide-y divide-slate-200/60">
                  {filtered.length === 0 ? (
  <div className="px-4 py-10 text-center">
    <div className="text-sm font-extrabold text-slate-900">
      No invoices found
    </div>
    <div className="mt-1 text-xs text-slate-500">
      Try clearing filters or changing search terms.
    </div>
  </div>
) : (
  filtered.map((inv) => {
    const isAlreadyPaid =
      statusLabel(inv) === "PAID" || statusLabel(inv) === "DRAFT";

    return (
      <div key={inv.invoice_id} className="px-4 py-3">
        <div className="grid grid-cols-12 items-center gap-2">
          <button
            onClick={() => {
              navigate(`/invoices/${inv.invoice_id}`)
              window.scrollTo?.(0, 0);
            }}
            className="col-span-3 text-left"
          >
            <div className="flex items-center gap-2">
              <div className="text-sm font-extrabold text-slate-900">
                {inv.invoice_number}
              </div>
              <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
            </div>
            <div className="mt-0.5 text-xs text-slate-500 truncate">
              Ref: {inv.reference_number}
            </div>
          </button>

          <div className="col-span-2">
            <div className="text-sm font-extrabold text-slate-900 truncate">
              {inv.customer_name}
            </div>
            <div className="mt-0.5 text-xs text-slate-500 truncate">
              {inv.email}
            </div>
          </div>

          <div className="col-span-2">
            <div className="text-sm font-semibold text-slate-800">
              {prettyDate(inv.date)}
            </div>
            <div className="mt-0.5 text-xs text-slate-500">
              Due: {prettyDate(inv.due_date)}
            </div>
          </div>

          <div className="col-span-2 text-center">
            <div className="text-sm font-extrabold text-slate-900">
              {moneyINR(inv.total)}
            </div>
            <div className="mt-0.5 text-xs text-slate-500">
              Bal: {moneyINR(inv.balance)}
            </div>
          </div>

          <div className="col-span-3 flex justify-end gap-2">
            <InternalButton
              size="sm"
              label="View"
              onClick={() => {
                navigate(`/invoices/${inv.invoice_id}`)
                window.scrollTo?.(0, 0);
              }}
            />

            {!isAlreadyPaid && role === 5 && (
              <BlueButton
                size="sm"
                label="Pay Now"
                disabled={!isPayEnabled(inv)}
                onClick={() => handlePayNow(inv)}
              />
            )}

            {/* <SecondaryLinkButton
              href={inv.invoice_url}
              label="Download"
              size="sm"
            /> */}
          </div>
        </div>
      </div>
    );
  })
)}

                </div>
              </div>

              {totalPages > 1 && (
                <div className="">
                    <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    />
                </div>
                )}

              {/* <div className="mt-4 text-xs text-slate-500">
                Tip: Use <span className="font-semibold">invoice_id</span> as key. Map warranty activation to{' '}
                <span className="font-semibold">reference_number</span>.
              </div> */}
            </div>
          </div>

          {/* <div className="mt-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Goelectronix Technologies Private Limited</div> */}
        </div>


      {/* When clicking View */}
        {/* <InternalButton
          size="sm"
          label="View"
          // onClick={() => onView(inv.invoice_id)}
        /> */}
    </div>
  );
}




// import { useEffect, useMemo, useState } from "react";
// import {
//   moneyINR,
//   prettyDate,
//   statusLabel,
//   statusTone,
//   isPayEnabled,
//   cn,
// } from "./invoiceUtils";
// import { Badge } from "../../../../Component/RetailerComponents/invoice/Badge";
// import { Stat } from "../../../../Component/RetailerComponents/invoice/Stat";
// import { InternalButton } from "../../../../Component/RetailerComponents/invoice/InternalButton";
// import { PrimaryLinkButton } from "../../../../Component/RetailerComponents/invoice/PrimaryLinkButton";
// import { SecondaryLinkButton } from "../../../../Component/RetailerComponents/invoice/SecondaryLinkButton";
// import Pagination from "../../../../Component/Common/Pagination";
// import { BASE_URL } from "../../../../config/api";
// import toast from "react-hot-toast";
// import BlueButton from "../../../../Component/Common/BlueButton";

// export default function InvoiceList({
//   invoices,
//   onView,
//   currentPage,
//   totalPages,
//   onPageChange,
// }) {
//   const [q, setQ] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [customerFilter, setCustomerFilter] = useState("all");
//   const [sortBy, setSortBy] = useState("date_desc");

//   const [paymentKeys, setPaymentKeys] = useState({
//           key_id: "",
//           key_secret: ""
//         });

//   const [keyLoading, setKeyLoading] = useState(false);
//     const [paymentSaving, setPaymentSaving] = useState(false);

//     const authUserString = localStorage.getItem("authUserData");
//     const authUser = authUserString ? JSON.parse(authUserString) : null;
//     const sessionId = authUser?.id;

//   /* ---------- customers ---------- */
//   const customers = useMemo(() => {
//     const map = new Map();
//     invoices.forEach((inv) => {
//       if (!map.has(inv.customer_id)) {
//         map.set(inv.customer_id, {
//           customer_id: inv.customer_id,
//           name: inv.customer_name,
//         });
//       }
//     });
//     return Array.from(map.values());
//   }, [invoices]);

//   /* ---------- filtered ---------- */
//   const filtered = useMemo(() => {
//     let out = invoices.slice();
//     const needle = q.trim().toLowerCase();

//     if (customerFilter !== "all") {
//       out = out.filter((x) => x.customer_id === customerFilter);
//     }

//     if (statusFilter !== "all") {
//       out = out.filter(
//         (x) => statusLabel(x).toLowerCase() === statusFilter
//       );
//     }

//     if (needle) {
//       out = out.filter((x) =>
//         [
//           x.invoice_number,
//           x.reference_number,
//           x.customer_name,
//         ]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase()
//           .includes(needle)
//       );
//     }

//     out.sort((a, b) => {
//       if (sortBy === "date_asc") return new Date(a.date) - new Date(b.date);
//       if (sortBy === "total_desc") return b.total - a.total;
//       if (sortBy === "total_asc") return a.total - b.total;
//       return new Date(b.date) - new Date(a.date);
//     });

//     return out;
//   }, [invoices, q, statusFilter, customerFilter, sortBy]);

//   /* ---------- KPIs ---------- */
//   const kpis = useMemo(() => {
//     const sum = (fn) => filtered.reduce((a, x) => a + fn(x), 0);
//     return {
//       total: sum((x) => x.total),
//       paid: sum((x) => x.payment_made),
//       balance: sum((x) => x.balance),
//       count: filtered.length,
//       overdueCount: filtered.filter(
//         (x) => statusLabel(x) === "OVERDUE"
//       ).length,
//     };
//   }, [filtered]);

// function Icon({ name, className }) {
//   const common = "fill-none stroke-current";
//   const props = {
//     className: cn("h-5 w-5", className),
//     viewBox: "0 0 24 24",
//     strokeWidth: 1.8,
//     strokeLinecap: "round",
//     strokeLinejoin: "round",
//   };

//   if (name === "link")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10.5 4.43" />
//         <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.4a5 5 0 0 0 7.07 7.07l.9-.9" />
//       </svg>
//     );

//   if (name === "copy")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <rect x="9" y="9" width="13" height="13" rx="2" />
//         <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
//       </svg>
//     );

//   if (name === "check")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M20 6 9 17l-5-5" />
//       </svg>
//     );

//   if (name === "user")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M20 21a8 8 0 1 0-16 0" />
//         <circle cx="12" cy="7" r="4" />
//       </svg>
//     );

//   if (name === "receipt")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M7 3h10a2 2 0 0 1 2 2v16l-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 0 1 2-2z" />
//         <path d="M9 7h6" />
//         <path d="M9 11h6" />
//         <path d="M9 15h4" />
//       </svg>
//     );

//   if (name === "pin")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M12 22s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
//         <circle cx="12" cy="11" r="2.5" />
//       </svg>
//     );

//   if (name === "clock")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <circle cx="12" cy="12" r="9" />
//         <path d="M12 7v6l4 2" />
//       </svg>
//     );

//   return (
//     <svg {...props} className={cn(props.className, common)}>
//       <circle cx="12" cy="12" r="9" />
//     </svg>
//   );
// }


// const fetchPaymentKeys = async () => {
//     try {
//       setKeyLoading(true);
  
//       const res = await fetch(`${BASE_URL}payment/keys`);
//       const data = await res.json();
  
//       if (Array.isArray(data) && data.length > 0) {
//         setPaymentKeys({
//           key_id: data[0].key_id,
//           key_secret: data[0].key_secret
//         });
//       } else {
//         console.error("Invalid keys response", data);
//       }
//     } catch (err) {
//       console.error("Failed to fetch payment keys", err);
//     } finally {
//       setKeyLoading(false);
//     }
//   };
  
//     useEffect(() => {
//       fetchPaymentKeys();
//   }, []);

//   const handlePayNow = async (inv) => {
//   if (!paymentKeys?.key_id) {
//     toast.error("Payment keys not loaded");
//     return;
//   }

//   // 💰 amount from invoice row
//   const amount = Number(inv.balance || 0);
//   const roundedAmount = Math.round(amount * 100); // paise

//   // 🧾 Notes built from ROW
//   const notes = {
//     project: "warranty",
//     service: "update",
//     imei1: inv.IMEI1,
//     imei2: inv.IMEI2,
//     serial:inv.serial,
//     product_id:inv.product_id,
//     model_id:inv.model_id,
//     company_id: authUser?.company_id,
//     retailer_id: sessionId,
//     agent_id: authUser?.agent_id,
//     amount: amount,
//     // warranty_id: row.warrantyId || null,
//     // invoice_id: row.id || null,
//     w_customer_id:inv.w_customer_id,
//     created_by: sessionId,
//     document_url: inv.billPhoto,
//     device_id: inv.rawid,
//   };

//   const options = {
//     key: paymentKeys.key_id,
//     amount: roundedAmount,
//     currency: "INR",
//     name: "GO Electronix",
//     description: `Invoice Payment - ${inv.invoice_Code || inv.id}`,
//     notes,

//     handler: async function (response) {
//       try {
//         setPaymentSaving(true);

//         console.log("✅ Razorpay Success:", response);
//         console.log("📦 Paid Row:", inv);

//         const payload = {
//           payment_id: response.razorpay_payment_id,

//           invoice_id: inv.id,
//           invoice_code: inv.invoice_Code,

//           amount,
//           customer_name: inv.customerName,
//           customer_code: inv.customerCode,

//           retailer_id: sessionId,
//           company_id: authUser?.company_id,

//           source: "invoice_pay_now",
//         };

//         console.log("🚀 Payment Payload:", payload);

//         toast.success("Payment successful 🎉");

//         // 🔁 Refresh invoices after payment
//         // fetchInvoices();
//         // fetchPayoutSummary();
//       } catch (err) {
//         console.error(err);
//         toast.error("Payment succeeded but processing failed");
//       } finally {
//         setPaymentSaving(false);
//       }
//     },

//     prefill: {
//       name: inv.customerName || "",
//       contact: inv.phone || "",
//     },

//     theme: { color: "#2563eb" },
//   };

//   const rzp = new window.Razorpay(options);
//   rzp.open();
// };

//   return (
//     <div className="min-h-screen bg-slate-50">
//       {/* 🔁 COPY YOUR EXISTING LIST JSX HERE */}

//       <div className="min-h-screen bg-slate-50">
//         {/* Header */}
//         <div className="relative overflow-hidden">
//           <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-sky-500 opacity-90" />
//           <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/15 blur-2xl" />
//           <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />

//           <div className="relative mx-auto  px-4 pb-6 pt-6 sm:px-6">
//             <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
//               <div className="flex items-start gap-3">
//                 <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 shadow-sm">
//                   <Icon name="receipt" className="h-6 w-6 text-white" />
//                 </div>
//                 <div>
//                   <h1 className="text-xl sm:text-2xl font-extrabold text-white">Invoices</h1>
//                   <div className="mt-1 text-sm text-white/85">Search • Filter • Pay Now</div>
//                 </div>
//               </div>

//               <div className="flex flex-wrap gap-2">
//                 <Badge tone="slate">Count: {kpis.count}</Badge>
//                 <Badge tone={kpis.overdueCount ? "red" : "green"}>Overdue: {kpis.overdueCount}</Badge>
//               </div>
//             </div>

//             {/* <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
//               <Stat label="Total" value={moneyINR(kpis.total)} sub="Filtered total" />
//               <Stat label="Paid" value={moneyINR(kpis.paid)} sub="Payments received" />
//               <Stat label="Balance" value={moneyINR(kpis.balance)} sub="Pending dues" />
//               <Stat label="Customers" value={String(customers.length)} sub="Unique" />
//             </div> */}
//           </div>
//         </div>

//         {/* Filters */}
//         <div className="mx-auto  px-4 py-6 sm:px-6">
//           <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 overflow-hidden">
//             <div className="p-5">
//               <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
//                 <div className="w-full lg:max-w-lg">
//                   <label className="text-xs font-bold text-slate-600">Search</label>
//                   <input
//                     value={q}
//                     onChange={(e) => setQ(e.target.value)}
//                     placeholder="Invoice no, customer, email, reference, phone..."
//                     className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:bg-white"
//                   />
//                 </div>

//                 <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 lg:w-auto">
//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Customer</label>
//                     <select
//                       value={customerFilter}
//                       onChange={(e) => setCustomerFilter(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="all">All Customers</option>
//                       {customers.map((c) => (
//                         <option key={c.customer_id} value={c.customer_id}>
//                           {c.name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Status</label>
//                     <select
//                       value={statusFilter}
//                       onChange={(e) => setStatusFilter(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="all">All</option>
//                       <option value="paid">Paid</option>
//                       <option value="overdue">Overdue</option>
//                       <option value="partial">Partial</option>
//                       <option value="draft">Draft</option>
//                       <option value="open">Open</option>
//                     </select>
//                   </div>

//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Sort</label>
//                     <select
//                       value={sortBy}
//                       onChange={(e) => setSortBy(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="date_desc">Date (Newest)</option>
//                       <option value="date_asc">Date (Oldest)</option>
//                       <option value="total_desc">Amount (High)</option>
//                       <option value="total_asc">Amount (Low)</option>
//                     </select>
//                   </div>
//                 </div>
//               </div>

//               {/* MOBILE LIST (cards) */}
//               <div className="mt-4 grid gap-3 md:hidden">
//                {filtered.length === 0 ? (
//   <div className="rounded-2xl border border-slate-200/60 bg-slate-50 p-6 text-center">
//     <div className="text-sm font-extrabold text-slate-900">No invoices found</div>
//     <div className="mt-1 text-xs text-slate-500">
//       Try clearing filters or changing search terms.
//     </div>
//   </div>
// ) : (
//   filtered.map((inv) => {
//     const isAlreadyPaid =
//       statusLabel(inv) === "PAID" || statusLabel(inv) === "DRAFT";

//     return (
//       <div
//         key={inv.invoice_id}
//         className="rounded-3xl bg-white border border-slate-200/60 shadow-sm p-2"
//       >
//         <div className="flex items-start justify-between gap-3">
//           <button
//             onClick={() => {
//               onView(inv.invoice_id);
//               window.scrollTo?.(0, 0);
//             }}
//             className="text-left"
//           >
//             <div className="text-sm font-extrabold text-slate-900">
//               {inv.invoice_number}
//             </div>
//             <div className="mt-0.5 text-xs text-slate-500 truncate">
//               {inv.customer_name}
//             </div>
//           </button>
//           <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
//         </div>

//         <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
//           <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//             <div className="text-[11px] text-slate-500 font-bold">Date</div>
//             <div className="mt-0.5 font-semibold text-slate-900">
//               {prettyDate(inv.date)}
//             </div>
//           </div>
//           <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//             <div className="text-[11px] text-slate-500 font-bold">Total</div>
//             <div className="mt-0.5 font-extrabold text-slate-900">
//               {moneyINR(inv.total)}
//             </div>
//           </div>
//           <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//             <div className="text-[11px] text-slate-500 font-bold">Paid</div>
//             <div className="mt-0.5 font-semibold text-slate-900">
//               {moneyINR(inv.payment_made)}
//             </div>
//           </div>
//           <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//             <div className="text-[11px] text-slate-500 font-bold">Balance</div>
//             <div
//               className={cn(
//                 "mt-0.5 font-extrabold",
//                 Number(inv.balance || 0) === 0
//                   ? "text-emerald-700"
//                   : "text-rose-700"
//               )}
//             >
//               {moneyINR(inv.balance)}
//             </div>
//           </div>
//         </div>

//         <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
//           <div className="text-xs text-slate-500 truncate">
//             Ref: {inv.reference_number}
//           </div>

//           <div className="flex flex-wrap items-center gap-2">
//             <InternalButton
//               size="sm"
//               label="View"
//               onClick={() => {
//                 onView(inv.invoice_id);
//                 window.scrollTo?.(0, 0);
//               }}
//             />

//             {!isAlreadyPaid && (
//               <PrimaryLinkButton
//                 href={inv.invoice_url}
//                 label="Pay Now"
//                 size="sm"
//                 enabled={isPayEnabled(inv)}
//               />
//             )}

//             {/* <SecondaryLinkButton
//               href={inv.invoice_url}
//               label="Download"
//               size="sm"
//             /> */}
//           </div>
//         </div>
//       </div>
//     );
//   })
// )}

//               </div>

//               {/* DESKTOP TABLE */}
//               <div className="mt-4 hidden md:block overflow-hidden rounded-2xl border border-slate-200/60">
//                 <div className="grid grid-cols-12 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
//                   <div className="col-span-3">Invoice</div>
//                   <div className="col-span-2">Customer</div>
//                   <div className="col-span-2">Date</div>
//                   <div className="col-span-2 text-center">Total</div>
//                   <div className="col-span-3 text-right">Actions</div>
//                 </div>

//                 <div className="divide-y divide-slate-200/60">
//                   {filtered.length === 0 ? (
//   <div className="px-4 py-10 text-center">
//     <div className="text-sm font-extrabold text-slate-900">
//       No invoices found
//     </div>
//     <div className="mt-1 text-xs text-slate-500">
//       Try clearing filters or changing search terms.
//     </div>
//   </div>
// ) : (
//   filtered.map((inv) => {
//     const isAlreadyPaid =
//       statusLabel(inv) === "PAID" || statusLabel(inv) === "DRAFT";

//     return (
//       <div key={inv.invoice_id} className="px-4 py-3">
//         <div className="grid grid-cols-12 items-center gap-2">
//           <button
//             onClick={() => {
//               onView(inv.invoice_id);
//               window.scrollTo?.(0, 0);
//             }}
//             className="col-span-3 text-left"
//           >
//             <div className="flex items-center gap-2">
//               <div className="text-sm font-extrabold text-slate-900">
//                 {inv.invoice_number}
//               </div>
//               <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
//             </div>
//             <div className="mt-0.5 text-xs text-slate-500 truncate">
//               Ref: {inv.reference_number}
//             </div>
//           </button>

//           <div className="col-span-2">
//             <div className="text-sm font-extrabold text-slate-900 truncate">
//               {inv.customer_name}
//             </div>
//             <div className="mt-0.5 text-xs text-slate-500 truncate">
//               {inv.email}
//             </div>
//           </div>

//           <div className="col-span-2">
//             <div className="text-sm font-semibold text-slate-800">
//               {prettyDate(inv.date)}
//             </div>
//             <div className="mt-0.5 text-xs text-slate-500">
//               Due: {prettyDate(inv.due_date)}
//             </div>
//           </div>

//           <div className="col-span-2 text-center">
//             <div className="text-sm font-extrabold text-slate-900">
//               {moneyINR(inv.total)}
//             </div>
//             <div className="mt-0.5 text-xs text-slate-500">
//               Bal: {moneyINR(inv.balance)}
//             </div>
//           </div>

//           <div className="col-span-3 flex justify-end gap-2">
//             <InternalButton
//               size="sm"
//               label="View"
//               onClick={() => {
//                 onView(inv.invoice_id);
//                 window.scrollTo?.(0, 0);
//               }}
//             />

//             {!isAlreadyPaid && (
//               <BlueButton
//                 size="sm"
//                 label="Pay Now"
//                 disabled={!isPayEnabled(inv)}
//                 onClick={() => handlePayNow(inv)}
//               />
//             )}

//             {/* <SecondaryLinkButton
//               href={inv.invoice_url}
//               label="Download"
//               size="sm"
//             /> */}
//           </div>
//         </div>
//       </div>
//     );
//   })
// )}

//                 </div>
//               </div>

//               {totalPages > 1 && (
//                 <div className="mx-auto px-4 pb-6 sm:px-6">
//                     <Pagination
//                     currentPage={currentPage}
//                     totalPages={totalPages}
//                     onPageChange={onPageChange}
//                     />
//                 </div>
//                 )}

//               <div className="mt-4 text-xs text-slate-500">
//                 Tip: Use <span className="font-semibold">invoice_id</span> as key. Map warranty activation to{' '}
//                 <span className="font-semibold">reference_number</span>.
//               </div>
//             </div>
//           </div>

//           <div className="mt-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Goelectronix Technologies Private Limited</div>
//         </div>
//       </div>

//       {/* When clicking View */}
//       <InternalButton
//         size="sm"
//         label="View"
//         // onClick={() => onView(inv.invoice_id)}
//       />
//     </div>
//   );
// }




// import { useMemo, useState } from "react";
// import {
//   moneyINR,
//   prettyDate,
//   statusLabel,
//   statusTone,
//   isPayEnabled,
//   cn,
// } from "./invoiceUtils";
// import { Badge } from "../../../../Component/RetailerComponents/invoice/Badge";
// import { Stat } from "../../../../Component/RetailerComponents/invoice/Stat";
// import { InternalButton } from "../../../../Component/RetailerComponents/invoice/InternalButton";
// import { PrimaryLinkButton } from "../../../../Component/RetailerComponents/invoice/PrimaryLinkButton";
// import { SecondaryLinkButton } from "../../../../Component/RetailerComponents/invoice/SecondaryLinkButton";
// import Pagination from "../../../../Component/Common/Pagination";

// export default function InvoiceList({
//   invoices,
//   onView,
//   currentPage,
//   totalPages,
//   onPageChange,
// }) {
//   const [q, setQ] = useState("");
//   const [statusFilter, setStatusFilter] = useState("all");
//   const [customerFilter, setCustomerFilter] = useState("all");
//   const [sortBy, setSortBy] = useState("date_desc");

//   /* ---------- customers ---------- */
//   const customers = useMemo(() => {
//     const map = new Map();
//     invoices.forEach((inv) => {
//       if (!map.has(inv.customer_id)) {
//         map.set(inv.customer_id, {
//           customer_id: inv.customer_id,
//           name: inv.customer_name,
//         });
//       }
//     });
//     return Array.from(map.values());
//   }, [invoices]);

//   /* ---------- filtered ---------- */
//   const filtered = useMemo(() => {
//     let out = invoices.slice();
//     const needle = q.trim().toLowerCase();

//     if (customerFilter !== "all") {
//       out = out.filter((x) => x.customer_id === customerFilter);
//     }

//     if (statusFilter !== "all") {
//       out = out.filter(
//         (x) => statusLabel(x).toLowerCase() === statusFilter
//       );
//     }

//     if (needle) {
//       out = out.filter((x) =>
//         [
//           x.invoice_number,
//           x.reference_number,
//           x.customer_name,
//         ]
//           .filter(Boolean)
//           .join(" ")
//           .toLowerCase()
//           .includes(needle)
//       );
//     }

//     out.sort((a, b) => {
//       if (sortBy === "date_asc") return new Date(a.date) - new Date(b.date);
//       if (sortBy === "total_desc") return b.total - a.total;
//       if (sortBy === "total_asc") return a.total - b.total;
//       return new Date(b.date) - new Date(a.date);
//     });

//     return out;
//   }, [invoices, q, statusFilter, customerFilter, sortBy]);

//   /* ---------- KPIs ---------- */
//   const kpis = useMemo(() => {
//     const sum = (fn) => filtered.reduce((a, x) => a + fn(x), 0);
//     return {
//       total: sum((x) => x.total),
//       paid: sum((x) => x.payment_made),
//       balance: sum((x) => x.balance),
//       count: filtered.length,
//       overdueCount: filtered.filter(
//         (x) => statusLabel(x) === "OVERDUE"
//       ).length,
//     };
//   }, [filtered]);

// function Icon({ name, className }) {
//   const common = "fill-none stroke-current";
//   const props = {
//     className: cn("h-5 w-5", className),
//     viewBox: "0 0 24 24",
//     strokeWidth: 1.8,
//     strokeLinecap: "round",
//     strokeLinejoin: "round",
//   };

//   if (name === "link")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10.5 4.43" />
//         <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.4a5 5 0 0 0 7.07 7.07l.9-.9" />
//       </svg>
//     );

//   if (name === "copy")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <rect x="9" y="9" width="13" height="13" rx="2" />
//         <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
//       </svg>
//     );

//   if (name === "check")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M20 6 9 17l-5-5" />
//       </svg>
//     );

//   if (name === "user")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M20 21a8 8 0 1 0-16 0" />
//         <circle cx="12" cy="7" r="4" />
//       </svg>
//     );

//   if (name === "receipt")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M7 3h10a2 2 0 0 1 2 2v16l-2-1-2 1-2-1-2 1-2-1-2 1-2-1-2 1V5a2 2 0 0 1 2-2z" />
//         <path d="M9 7h6" />
//         <path d="M9 11h6" />
//         <path d="M9 15h4" />
//       </svg>
//     );

//   if (name === "pin")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <path d="M12 22s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
//         <circle cx="12" cy="11" r="2.5" />
//       </svg>
//     );

//   if (name === "clock")
//     return (
//       <svg {...props} className={cn(props.className, common)}>
//         <circle cx="12" cy="12" r="9" />
//         <path d="M12 7v6l4 2" />
//       </svg>
//     );

//   return (
//     <svg {...props} className={cn(props.className, common)}>
//       <circle cx="12" cy="12" r="9" />
//     </svg>
//   );
// }

//   return (
//     <div className="min-h-screen bg-slate-50">
//       {/* 🔁 COPY YOUR EXISTING LIST JSX HERE */}

//       <div className="min-h-screen bg-slate-50">
//         {/* Header */}
//         <div className="relative overflow-hidden">
//           <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-sky-500 opacity-90" />
//           <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-white/15 blur-2xl" />
//           <div className="absolute -bottom-28 -right-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />

//           <div className="relative mx-auto  px-4 pb-6 pt-6 sm:px-6">
//             <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
//               <div className="flex items-start gap-3">
//                 <div className="rounded-2xl bg-white/15 backdrop-blur border border-white/20 p-3 shadow-sm">
//                   <Icon name="receipt" className="h-6 w-6 text-white" />
//                 </div>
//                 <div>
//                   <h1 className="text-xl sm:text-2xl font-extrabold text-white">Invoices</h1>
//                   <div className="mt-1 text-sm text-white/85">Search • Filter • Pay Now</div>
//                 </div>
//               </div>

//               <div className="flex flex-wrap gap-2">
//                 <Badge tone="slate">Count: {kpis.count}</Badge>
//                 <Badge tone={kpis.overdueCount ? "red" : "green"}>Overdue: {kpis.overdueCount}</Badge>
//               </div>
//             </div>

//             <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
//               <Stat label="Total" value={moneyINR(kpis.total)} sub="Filtered total" />
//               <Stat label="Paid" value={moneyINR(kpis.paid)} sub="Payments received" />
//               <Stat label="Balance" value={moneyINR(kpis.balance)} sub="Pending dues" />
//               <Stat label="Customers" value={String(customers.length)} sub="Unique" />
//             </div>
//           </div>
//         </div>

//         {/* Filters */}
//         <div className="mx-auto  px-4 py-6 sm:px-6">
//           <div className="rounded-3xl bg-white shadow-sm border border-slate-200/60 overflow-hidden">
//             <div className="p-5">
//               <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
//                 <div className="w-full lg:max-w-lg">
//                   <label className="text-xs font-bold text-slate-600">Search</label>
//                   <input
//                     value={q}
//                     onChange={(e) => setQ(e.target.value)}
//                     placeholder="Invoice no, customer, email, reference, phone..."
//                     className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none focus:bg-white"
//                   />
//                 </div>

//                 <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-3 lg:w-auto">
//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Customer</label>
//                     <select
//                       value={customerFilter}
//                       onChange={(e) => setCustomerFilter(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="all">All Customers</option>
//                       {customers.map((c) => (
//                         <option key={c.customer_id} value={c.customer_id}>
//                           {c.name}
//                         </option>
//                       ))}
//                     </select>
//                   </div>

//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Status</label>
//                     <select
//                       value={statusFilter}
//                       onChange={(e) => setStatusFilter(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="all">All</option>
//                       <option value="paid">Paid</option>
//                       <option value="overdue">Overdue</option>
//                       <option value="partial">Partial</option>
//                       <option value="draft">Draft</option>
//                       <option value="open">Open</option>
//                     </select>
//                   </div>

//                   <div>
//                     <label className="text-xs font-bold text-slate-600">Sort</label>
//                     <select
//                       value={sortBy}
//                       onChange={(e) => setSortBy(e.target.value)}
//                       className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-900 outline-none"
//                     >
//                       <option value="date_desc">Date (Newest)</option>
//                       <option value="date_asc">Date (Oldest)</option>
//                       <option value="total_desc">Amount (High)</option>
//                       <option value="total_asc">Amount (Low)</option>
//                     </select>
//                   </div>
//                 </div>
//               </div>

//               {/* MOBILE LIST (cards) */}
//               <div className="mt-4 grid gap-3 md:hidden">
//                 {filtered.length === 0 ? (
//                   <div className="rounded-2xl border border-slate-200/60 bg-slate-50 p-6 text-center">
//                     <div className="text-sm font-extrabold text-slate-900">No invoices found</div>
//                     <div className="mt-1 text-xs text-slate-500">Try clearing filters or changing search terms.</div>
//                   </div>
//                 ) : (
//                   filtered.map((inv) => (
//                     <div
//                       key={inv.invoice_id}
//                       className="rounded-3xl bg-white border border-slate-200/60 shadow-sm p-4"
//                     >
//                       <div className="flex items-start justify-between gap-3">
//                         <button
//                           onClick={() => {
//                             onView(inv.invoice_id);
//                             window.scrollTo?.(0, 0);
//                           }}
//                           className="text-left"
//                         >
//                           <div className="text-sm font-extrabold text-slate-900">{inv.invoice_number}</div>
//                           <div className="mt-0.5 text-xs text-slate-500 truncate">{inv.customer_name}</div>
//                         </button>
//                         <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
//                       </div>

//                       <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
//                         <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//                           <div className="text-[11px] text-slate-500 font-bold">Date</div>
//                           <div className="mt-0.5 font-semibold text-slate-900">{prettyDate(inv.date)}</div>
//                         </div>
//                         <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//                           <div className="text-[11px] text-slate-500 font-bold">Total</div>
//                           <div className="mt-0.5 font-extrabold text-slate-900">{moneyINR(inv.total)}</div>
//                         </div>
//                         <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//                           <div className="text-[11px] text-slate-500 font-bold">Paid</div>
//                           <div className="mt-0.5 font-semibold text-slate-900">{moneyINR(inv.payment_made)}</div>
//                         </div>
//                         <div className="rounded-2xl bg-slate-50 border border-slate-200/60 p-3">
//                           <div className="text-[11px] text-slate-500 font-bold">Balance</div>
//                           <div
//                             className={cn(
//                               "mt-0.5 font-extrabold",
//                               Number(inv.balance || 0) === 0 ? "text-emerald-700" : "text-rose-700"
//                             )}
//                           >
//                             {moneyINR(inv.balance)}
//                           </div>
//                         </div>
//                       </div>

//                       <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
//                         <div className="text-xs text-slate-500 truncate">Ref: {inv.reference_number}</div>
//                         <div className="flex flex-wrap items-center gap-2">
//                           <InternalButton
//                             size="sm"
//                             label="View"
//                             onClick={() => {
//                               onView(inv.invoice_id)
//                               window.scrollTo?.(0, 0);
//                             }}
//                           />
//                           <PrimaryLinkButton
//                             href={inv.invoice_url}
//                             label="Pay Now"
//                             size="sm"
//                             enabled={isPayEnabled(inv)}
//                           />
//                           <SecondaryLinkButton href={inv.invoice_url} label="Download" size="sm" />
//                         </div>
//                       </div>
//                     </div>
//                   ))
//                 )}
//               </div>

//               {/* DESKTOP TABLE */}
//               <div className="mt-4 hidden md:block overflow-hidden rounded-2xl border border-slate-200/60">
//                 <div className="grid grid-cols-12 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600">
//                   <div className="col-span-3">Invoice</div>
//                   <div className="col-span-2">Customer</div>
//                   <div className="col-span-2">Date</div>
//                   <div className="col-span-2 text-center">Total</div>
//                   <div className="col-span-3 text-right">Actions</div>
//                 </div>

//                 <div className="divide-y divide-slate-200/60">
//                   {filtered.length === 0 ? (
//                     <div className="px-4 py-10 text-center">
//                       <div className="text-sm font-extrabold text-slate-900">No invoices found</div>
//                       <div className="mt-1 text-xs text-slate-500">Try clearing filters or changing search terms.</div>
//                     </div>
//                   ) : (
//                     filtered.map((inv) => (
//                       <div key={inv.invoice_id} className="px-4 py-3">
//                         <div className="grid grid-cols-12 items-center gap-2">
//                           <button
//                             onClick={() => {
//                               onView(inv.invoice_id)
//                               window.scrollTo?.(0, 0);
//                             }}
//                             className="col-span-3 text-left"
//                           >
//                             <div className="flex items-center gap-2">
//                               <div className="text-sm font-extrabold text-slate-900">{inv.invoice_number}</div>
//                               <Badge tone={statusTone(inv)}>{statusLabel(inv)}</Badge>
//                             </div>
//                             <div className="mt-0.5 text-xs text-slate-500 truncate">Ref: {inv.reference_number}</div>
//                           </button>

//                           <div className="col-span-2">
//                             <div className="text-sm font-extrabold text-slate-900 truncate">{inv.customer_name}</div>
//                             <div className="mt-0.5 text-xs text-slate-500 truncate">{inv.email}</div>
//                           </div>

//                           <div className="col-span-2">
//                             <div className="text-sm font-semibold text-slate-800">{prettyDate(inv.date)}</div>
//                             <div className="mt-0.5 text-xs text-slate-500">Due: {prettyDate(inv.due_date)}</div>
//                           </div>

//                           <div className="col-span-2 text-center">
//                             <div className="text-sm font-extrabold text-slate-900">{moneyINR(inv.total)}</div>
//                             <div className="mt-0.5 text-xs text-slate-500">Bal: {moneyINR(inv.balance)}</div>
//                           </div>

//                           <div className="col-span-3 flex justify-end gap-2">
//                             <InternalButton
//                               size="sm"
//                               label="View"
//                               onClick={() => {
//                                 onView(inv.invoice_id)
//                                 window.scrollTo?.(0, 0);
//                               }}
//                             />
//                             <PrimaryLinkButton
//                               href={inv.invoice_url}
//                               label="Pay Now"
//                               size="sm"
//                               enabled={isPayEnabled(inv)}
//                             />
//                             <SecondaryLinkButton href={inv.invoice_url} label="Download" size="sm" />
//                           </div>
//                         </div>
//                       </div>
//                     ))
//                   )}
//                 </div>
//               </div>

//               {totalPages > 1 && (
//                 <div className="mx-auto px-4 pb-6 sm:px-6">
//                     <Pagination
//                     currentPage={currentPage}
//                     totalPages={totalPages}
//                     onPageChange={onPageChange}
//                     />
//                 </div>
//                 )}

//               <div className="mt-4 text-xs text-slate-500">
//                 Tip: Use <span className="font-semibold">invoice_id</span> as key. Map warranty activation to{' '}
//                 <span className="font-semibold">reference_number</span>.
//               </div>
//             </div>
//           </div>

//           <div className="mt-6 text-center text-xs text-slate-400">© {new Date().getFullYear()} Goelectronix Technologies Private Limited</div>
//         </div>
//       </div>

//       {/* When clicking View */}
//       <InternalButton
//         size="sm"
//         label="View"
//         // onClick={() => onView(inv.invoice_id)}
//       />
//     </div>
//   );
// }
